const REDIS_PRESENCE_URL =
  'https://www.planetspark.in/api/v1s/classroom/prebuilt/room_presence_redis';

/** Why a participant left the `new_dailyco_waiting` screen and called daily.join(). */
export type LobbyReleaseReason =
  /** The parent page relayed the ActionCable `dailyco_user_joined` broadcast. */
  | 'cable'
  /** The 30-45 s Redis poll saw the other party. */
  | 'poll'
  /** The other party was already registered when Join was clicked. */
  | 'direct'
  /**
   * The poll got no usable ANSWER -- a throw, not an error status: network
   * drop, DNS, CORS, the 15 s abort, or a 200 whose body will not parse.
   * Joined fail-open.
   *
   * "Threw" does not mean "never reached galaxy". It does for a network drop
   * or DNS; it does NOT for CORS, for the abort (which means galaxy was
   * merely SLOW -- there the following join will very likely succeed, the
   * opposite of what the rest of this note predicts), or for a body that
   * fails to parse. So a spike in this reason may mean galaxy got slow, not
   * that clients went offline -- check endpoint latency before concluding.
   * Kept distinct from `error_fallback` (which is galaxy answering with an
   * error status) because the two have different causes and different
   * expected outcomes -- though NOT uniformly, which is the trap: where the
   * connection itself is down the join will not land either, so expect a
   * watchdog resume rather than a successful call, but for the abort and CORS
   * subclasses above the join usually DOES land.
   *
   * Structurally UNDER-recorded, and more so than `error_fallback`: this row
   * travels over the connection that just failed, and the per-mount report
   * budget is spent whether or not the POST lands. Build dashboards on
   * `recovered`, which is the one report that goes over a network shown to
   * work. The exception is a CORS rejection, where the request does reach
   * galaxy -- there every retry lands, so that subclass is over-counted
   * instead.
   */
  | 'request_failed'
  /**
   * Galaxy ANSWERED the presence call with an error status, and the client
   * joined fail-open. A throw -- no answer at all -- is `request_failed`
   * above, so this reason no longer covers one anywhere.
   */
  | 'error_fallback';

export interface RoomPresenceResult {
  hasNonOwners: boolean;
  canJoin?: boolean;
  isRoomActive?: boolean;
  totalCount: number;
  error?: string;
  /** Set by galaxy when Daily's live presence, not Redis, granted the join. */
  presenceSource?: 'daily';
}

type PresenceAction = 'join' | 'leave' | 'check' | 'release' | 'poll_failure';

/**
 * Which kind of poll-failure report this is.
 *
 * `ongoing` travels over the network that is already failing, so it arrives
 * for a partial outage (5xx, CORS) and is lost for a total one -- a lower
 * bound, like `error_fallback`. `recovered` is posted when the run ENDS, over
 * a network that demonstrably works, so it is the reliable one and carries the
 * whole run. Since M-2 it has two senders, and the phase does not say which:
 * the first poll that succeeds again (a genuine recovery), and a fail-open
 * join that lands (a rescue -- the run ended because the participant got in,
 * not because presence came back). Tell them apart by whether a `released`
 * row with reason `request_failed` sits immediately before it for the same
 * room and participant; galaxy's model header says the same. Merged on
 * purpose rather than adding a third phase, which would need a paired change
 * to galaxy's POLL_FAILURE_PHASES whitelist to be recorded at all.
 */
export type LobbyPollFailurePhase = 'ongoing' | 'recovered';

/**
 * Consecutive swallowed polls before the first `ongoing` report is sent.
 *
 * Must stay >= galaxy's POLL_FAILURE_ALERT_AFTER (DailyCoLobbyMonitor), which
 * drops a report below its own threshold. At 2 against galaxy's 3 the
 * in-outage warning could never fire at all: the only surviving alert path was
 * a `recovered` report, i.e. after the outage had already healed.
 */
export const POLL_FAILURE_REPORT_AFTER = 3;

/**
 * Consecutive poll throws before the waiting screen joins fail-open.
 *
 * 1 = the first failure releases -- but ONLY from a tab that was visible when
 * the request went out. A throw on a request issued while the tab was hidden
 * is counted and reported and does NOT release, however many of them there
 * are; see the guard in OwnerLiveEventWaiting's runPoll. Changing this number
 * does not change that, and the guard is the reason 1 is defensible at all.
 *
 * The call this encodes: a false hold is worse than a false release, so a
 * client that cannot ask whether its partner is there is let through rather
 * than held.
 *
 * Measured cost, and the two counts do NOT agree -- reconcile before quoting
 * either: 7 recorded failure runs over the pilot's first nine days (mine), and
 * 9 over 15-17 Oct 2026 across 8 rooms (adityan's, on PR #13). A larger count
 * over a shorter window means one query is wrong; the 9 is the one that drove
 * the hidden-tab guard, since it broke the runs down as 6 single throws
 * healing in 4-50 s. Both agree on the part that matters: EVERY run began
 * while the counterpart was absent, so a release here usually puts someone
 * into an empty room for a while -- the accepted trade, not a surprise.
 *
 * 2, not 1, and the reason is correlation rather than caution. The 15 s abort
 * counts as a throw, so at 1 a SINGLE galaxy latency excursion past 15 s
 * released every waiting client in every room on the same tick -- the
 * hidden-tab guard does nothing for that, because those tabs are visible and
 * their requests are real. At 2 the same excursion has to persist across a
 * whole 30-45 s interval before anyone is released, which is the difference
 * between "one slow request" and "presence is actually down".
 *
 * It costs a genuine outage one extra interval and nothing else, and it still
 * rescues the case this was built for: `demo_6330106`'s second failure came
 * ~40 s after the first, still before the parent arrived. Both reviews of #13
 * recommended it on the measured base rate; the owner took it on 21 Oct 2026
 * once the correlated-release argument was on the table.
 *
 * Lowering it back to 1 is a one-character change and the release still
 * reports `request_failed`, so the two policies stay comparable in the data --
 * but read the paragraph above first.
 */
export const JOIN_AFTER_FAILED_POLLS = 2;

/**
 * How long a presence request may take before it is aborted.
 *
 * Must stay well under REDIS_PRESENCE_POLL_MIN_MS. `fetch` has no timeout of
 * its own, so a connection that neither answers nor errors used to leave the
 * waiting screen's `await` pending until the browser gave up -- about 300 s in
 * Chrome. Nothing threw, so the poll-failure report stayed silent too: a hung
 * poll was indistinguishable from a healthy one. Aborting turns it into a
 * throw, which the loop already counts and reports.
 */
export const PRESENCE_REQUEST_TIMEOUT_MS = 15_000;

export const REDIS_PRESENCE_POLL_MIN_MS = 30_000;
export const REDIS_PRESENCE_POLL_MAX_MS = 45_000;

export const getRandomPollInterval = (minMs: number, maxMs: number) =>
  Math.floor(minMs + Math.random() * (maxMs - minMs));

export const canJoinFromPresence = (result: RoomPresenceResult) =>
  result.canJoin ?? result.hasNonOwners;

const TAB_ID_STORAGE_KEY = 'ps_presence_tab_id';

const newTabId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;

/**
 * An id for this browser tab, sent with every presence call. Galaxy uses it to
 * tell two tabs of one participant apart, so that leaving the call in one tab
 * does not remove the participant while the other tab is still in it.
 *
 * It lives in sessionStorage, which a tab keeps across a reload. That matters:
 * a reload during the call sends a new join and no leave, so an id that
 * changed on every load would leave a stale entry behind each time. For the
 * same reason there is no in-memory fallback. Without a working sessionStorage
 * no tab id is sent, and galaxy treats this browser's tabs as one, as before.
 */
export const getPresenceTabId = (): string | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    const storage = window.sessionStorage;
    const stored = storage.getItem(TAB_ID_STORAGE_KEY);
    if (stored) return stored;

    const tabId = newTabId();
    storage.setItem(TAB_ID_STORAGE_KEY, tabId);
    // A store that accepts the write and then forgets it would hand out a new
    // id on every call, which is worse than none.
    return storage.getItem(TAB_ID_STORAGE_KEY) === tabId ? tabId : undefined;
  } catch {
    return undefined;
  }
};

const buildPresenceFormData = (
  roomId?: string,
  roomName?: string,
  action: PresenceAction = 'check',
  userId?: string,
  isOwner?: boolean,
  reason?: LobbyReleaseReason
) => {
  const formData = new FormData();
  formData.append('room_id', roomId ?? '');
  formData.append('room_name', roomName ?? '');
  formData.append('meeting_platform', 'dailyco');
  formData.append('api_token', 'Dy2MwYGdmDxxB9PZQE7yyfVBpWix7zWMrpgX');
  formData.append('app_key', 'DailyCoPrebuilt');
  formData.append('app_version', '1.0');
  formData.append('action_type', action);
  formData.append('is_owner', isOwner ? 'true' : 'false');
  if (userId) {
    formData.append('user_id', userId);
  }
  if (reason) {
    formData.append('reason', reason);
  }
  const tabId = getPresenceTabId();
  if (tabId) {
    formData.append('tab_id', tabId);
  }
  return formData;
};

/**
 * Tells galaxy that this participant left the waiting screen and is calling
 * daily.join(), and why. Telemetry only: it never blocks the join, and a
 * failure is ignored. `keepalive` so the beacon survives the navigation a
 * join may trigger.
 *
 * `presence` is the presence answer behind the release, when there is one.
 * Its `presenceSource` is forwarded, so galaxy can tell a release granted by
 * its Daily-presence fallback from one granted by the Redis sets.
 *
 * Known limit: a fail-open release -- `error_fallback` or `request_failed` --
 * is posted to the very endpoint whose failure it reports, so during a galaxy
 * outage or a token mismatch it is lost with it. It binds hardest on the
 * `request_failed` subclasses where the connection itself is down; for the
 * CORS and abort subclasses galaxy IS reachable, so there the report usually
 * lands -- which is the same reason those subclasses over-count.
 * A browser-side Sentry capture was added in `8e9ed7b` to
 * cover that and removed again: `lib/sentry.ts` only calls `Sentry.init` when
 * `NEXT_PUBLIC_SENTRY_ENVIRONMENT` is set, nothing sets it in the deployed
 * build, and the one DSN in this app is the upstream Daily project's. Until a
 * PlanetSpark project exists, fail-open joins during a galaxy outage are
 * unmeasured. (PR #2, review of 5 Oct 2026, M1.)
 */
export async function reportLobbyRelease(
  roomId: string | undefined,
  roomName: string | undefined,
  userId: string | undefined,
  isOwner: boolean | undefined,
  reason: LobbyReleaseReason,
  presence?: Pick<RoomPresenceResult, 'error' | 'presenceSource'> | null
): Promise<void> {
  if (!roomName) return;
  try {
    const body = buildPresenceFormData(
      roomId,
      roomName,
      'release',
      userId,
      isOwner,
      reason
    );
    if (presence?.presenceSource) {
      body.append('presence_source', presence.presenceSource);
    }
    await fetch(REDIS_PRESENCE_URL, {
      method: 'POST',
      body,
      keepalive: true,
    });
  } catch {
    // Telemetry must never affect the join.
  }
}

/**
 * Tells galaxy that the waiting screen's presence poll is throwing.
 *
 * Without this a failing poll is invisible: `checkRedis` swallows the throw,
 * nothing is written, and this app's Sentry is inert in the deployed build
 * (see `reportLobbyRelease` above). A poll that was never made and a poll that
 * failed every time therefore produced identical data -- which is why a 3m42s
 * hold in `demo_6310253` on 12 Oct 2026 could not be explained at all.
 *
 * Telemetry only, and deliberately not retried: it must never delay the poll
 * loop it is reporting on. Galaxy does NOT refresh the waiting mark from this
 * report -- a client that cannot reach the endpoint is not evidence that it is
 * alive, and refreshing here would make staleness unfalsifiable.
 */
export async function reportLobbyPollFailure(
  roomId: string | undefined,
  roomName: string | undefined,
  userId: string | undefined,
  isOwner: boolean | undefined,
  phase: LobbyPollFailurePhase,
  failures: number,
  failureSeconds: number
): Promise<void> {
  if (!roomName || failures <= 0) return;
  try {
    const body = buildPresenceFormData(
      roomId,
      roomName,
      'poll_failure',
      userId,
      isOwner
    );
    body.append('failure_phase', phase);
    body.append('failure_count', String(failures));
    body.append(
      'failure_seconds',
      String(Math.max(0, Math.round(failureSeconds)))
    );
    await fetch(REDIS_PRESENCE_URL, {
      method: 'POST',
      body,
      keepalive: true,
    });
  } catch {
    // The report failing is itself unremarkable: the network it needs is the
    // one that just failed. The `recovered` report covers this run instead.
  }
}

const postRedisPresence = async (
  roomId: string | undefined,
  roomName: string | undefined,
  action: PresenceAction,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> => {
  // An abort surfaces as a throw, which is what every caller already handles:
  // the waiting screen counts and reports it, and the Join click joins
  // fail-open rather than leaving the button dead.
  const controller =
    typeof AbortController === 'undefined' ? undefined : new AbortController();
  const timeout = controller
    ? setTimeout(() => controller.abort(), PRESENCE_REQUEST_TIMEOUT_MS)
    : undefined;

  // The body read is INSIDE the try, and awaited, so the deadline covers it.
  // Clearing the timer as soon as `fetch` resolved would have left
  // `response.json()` reading a stalled body with the timer gone and the
  // signal spent: a response whose headers arrive and whose body never does
  // would hang for ever. That is survivable without a caller-side latch, and
  // fatal with one -- `runPoll`'s `finally` would never run, so its in-flight
  // flag would stay set and kill both the interval and the visibility check,
  // silently and with no throw to report. Exactly the failure this file is
  // meant to end.
  try {
    const response = await fetch(REDIS_PRESENCE_URL, {
      method: 'POST',
      body: buildPresenceFormData(roomId, roomName, action, userId, isOwner),
      signal: controller?.signal,
    });

    if (!response.ok) {
      return {
        hasNonOwners: false,
        canJoin: false,
        isRoomActive: false,
        totalCount: 0,
        error: `API error: ${response.status}`,
      };
    }

    return await response.json();
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
};

/**
 * Registers the current user in the Redis-backed room presence cache.
 */
export async function registerRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> {
  return postRedisPresence(roomId, roomName, 'join', userId, isOwner);
}

/**
 * Removes one matching entry for the current user from Redis room presence.
 *
 * Deliberately has no abort deadline: this is sent on `pagehide`, where the
 * page is going away and a timer would not outlive it. It also has its own
 * `fetch` rather than going through `postRedisPresence`, so the deadline
 * added there does not reach it.
 */
export async function leaveRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  options?: { keepalive?: boolean; isOwner?: boolean }
): Promise<RoomPresenceResult> {
  const response = await fetch(REDIS_PRESENCE_URL, {
    method: 'POST',
    body: buildPresenceFormData(
      roomId,
      roomName,
      'leave',
      userId,
      options?.isOwner
    ),
    keepalive: options?.keepalive,
  });

  if (!response.ok) {
    return {
      hasNonOwners: false,
      canJoin: false,
      isRoomActive: false,
      totalCount: 0,
      error: `API error: ${response.status}`,
    };
  }

  return response.json();
}

/**
 * Fetches room presence from the Redis-backed cache endpoint.
 */
export async function fetchRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> {
  return postRedisPresence(roomId, roomName, 'check', userId, isOwner);
}
